import { Flex, Tag, Td, Toggle } from '@alpine-iq/ui-library';

import { CampaignsType } from '@/types';
import { Icon } from '@alpine-iq/ui-library';
import React from 'react';
import HelpTooltip from '@/components/HelpTooltip';
import { useCampaignsStore } from '@/stores';
import AlertModal from '@/components/ux/AlertModal/AlertModal';
import { createPortal } from 'react-dom';
import { capturePosthogEvent } from '@/helpers';

export default function CampaignStatusCell(props: { campaign: CampaignsType }) {
	const { campaign } = props;
	const [confirmSetActiveStatus, setConfirmSetActiveStatus] = React.useState(false);
	const { setActiveStatus } = useCampaignsStore();

	const getComponent = React.useCallback(() => {
		if (campaign.draft) {
			return <Tag variant="gray">Draft</Tag>;
		}

		if (campaign.isCoMarketing) {
			return <Tag variant="gray">Co-Marketing</Tag>;
		}

		const tooltip = campaign.isActive ? (
			<>
				<b>Active</b>
				<p>This campaign is currently active and sending messages.</p>
			</>
		) : (
			<>
				<b>Inactive</b>
				<p>This campaign is currently inactive and not sending messages.</p>
			</>
		);

		return (
			<>
				<HelpTooltip tooltip={tooltip}>
					<div>
						<Toggle
							toggled={campaign.isActive}
							onChange={() => {
								setConfirmSetActiveStatus(true);
								capturePosthogEvent('Campaigns - Prompt to Change Status', {
									campaignId: campaign.id,
									isActive: campaign.isActive,
								});
							}}
						/>
					</div>
				</HelpTooltip>
			</>
		);
	}, [campaign.isActive, campaign.isCoMarketing, campaign.draft]);

	const renderModals = () => {
		return createPortal(
			<>
				<AlertModal
					title="Set Active Status"
					description={
						<>
							Are you sure you want to <b>{campaign.isActive ? 'deactivate' : 'activate'}</b> this campaign?
						</>
					}
					open={confirmSetActiveStatus}
					onConfirm={() => {
						capturePosthogEvent('Campaigns - Set Active Status', {
							campaignId: campaign.id,
							isActive: campaign.isActive,
						});
						return setActiveStatus(campaign.id, !campaign.isActive);
					}}
					onClose={() => setConfirmSetActiveStatus(false)}
				/>
			</>,
			document.body,
		);
	};

	return (
		<>
			{renderModals() as React.ReactNode}

			<Td style={{ width: 100 }}>
				<Flex
					align="center"
					gap={8}
				>
					{campaign.isLocked && (
						<HelpTooltip tooltip="This campaign has been locked and cannot be edited.">
							<div>
								<Icon
									type="ADMIN"
									color="var(--text-secondary)"
								/>
							</div>
						</HelpTooltip>
					)}
					{getComponent()}
				</Flex>
			</Td>
		</>
	);
}
